// ============================================
// Financial Dashboard
// Main orchestrator component for admin financial view
// ============================================

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Lock, AlertCircle, Rocket, ChevronDown, ChevronUp } from 'lucide-react';
import { useFinancialData } from './useFinancialData';
import { DateRangeFilter } from './DateRangeFilter';
import { KpiCards } from './KpiCards';
import { OperationsView } from './OperationsView';
import { RevenueByTimeTab } from './RevenueByTimeTab';
import { RevenueByBundleTab } from './RevenueByBundleTab';
import { OrderPipelineTab } from './OrderPipelineTab';
import { ExportButton } from './ExportButton';
import { InvestorView } from './InvestorView';
import { CfoView } from './CfoView';
import { ConfigPanel } from './ConfigPanel';

type TabId =
  | 'operations'
  | 'revenue-time'
  | 'by-bundle'
  | 'pipeline'
  | 'investor'
  | 'cfo'
  | 'config';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'operations', label: 'Operations' },
  { id: 'revenue-time', label: 'Revenue by Time' },
  { id: 'by-bundle', label: 'By Bundle' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'investor', label: 'Investor' },
  { id: 'cfo', label: 'CFO' },
  { id: 'config', label: 'Config' },
];

export function FinancialDashboard() {
  const {
    metrics,
    loading,
    error,
    authRequired,
    dateRange,
    setDateRange,
    authenticate,
  } = useFinancialData();

  const [activeTab, setActiveTab] = useState<TabId>('operations');
  const [authPassword, setAuthPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showSetup, setShowSetup] = useState(true);

  // --- Auth handler ---
  async function handleAuthenticate(e: React.FormEvent) {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      await authenticate(authPassword);
      setAuthPassword('');
    } catch (err) {
      setAuthError(
        err instanceof Error ? err.message : 'Authentication failed',
      );
    } finally {
      setAuthLoading(false);
    }
  }

  // --- Auth prompt (inline card) ---
  if (authRequired) {
    return (
      <div className="space-y-8">
        <h2 className="text-3xl font-display text-gold">Financial Dashboard</h2>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-md bg-bg-card border border-bg-tertiary rounded-xl p-6 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center">
              <Lock size={20} className="text-gold" />
            </div>
            <div>
              <h3 className="text-lg font-display text-text-primary">
                Admin Authentication Required
              </h3>
              <p className="text-sm text-text-muted">
                Enter vendor password to access financial data
              </p>
            </div>
          </div>

          <form onSubmit={handleAuthenticate} className="space-y-4">
            <input
              type="password"
              value={authPassword}
              onChange={(e) => {
                setAuthPassword(e.target.value);
                if (authError) setAuthError('');
              }}
              placeholder="Enter password"
              className="w-full px-4 py-3 rounded-xl bg-bg-elevated border border-bg-tertiary text-text-primary text-base placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/50 transition-colors"
              autoFocus
              disabled={authLoading}
            />

            {authError && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-critical text-sm"
              >
                <AlertCircle size={16} />
                {authError}
              </motion.div>
            )}

            <button
              type="submit"
              disabled={authLoading || !authPassword}
              className="w-full py-3 rounded-xl bg-gold text-bg-deep font-medium text-base hover:bg-gold/90 transition-colors focus:outline-none focus:ring-2 focus:ring-gold/50 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {authLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Authenticating...
                </>
              ) : (
                'Authenticate'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // --- Loading state ---
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <Loader2 size={32} className="text-gold animate-spin" />
        <p className="text-text-muted text-base">Loading financial data...</p>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="space-y-8">
        <h2 className="text-3xl font-display text-gold">Financial Dashboard</h2>
        <div className="bg-critical/10 border border-critical/30 rounded-xl p-5 flex items-center gap-3">
          <AlertCircle size={24} className="text-critical flex-shrink-0" />
          <p className="text-base text-critical">{error}</p>
        </div>
      </div>
    );
  }

  // --- Empty state (no orders yet — show full setup guide) ---
  if (metrics.allOrders.length === 0) {
    return (
      <div className="space-y-8">
        <h2 className="text-3xl font-display text-gold">Financial Dashboard</h2>
        <SetupBanner expanded />
      </div>
    );
  }

  // --- Main dashboard ---
  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="text-3xl font-display text-gold">Financial Dashboard</h2>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSetup(!showSetup)}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-bg-elevated border border-bg-tertiary text-text-secondary hover:text-gold transition-colors flex items-center gap-1.5"
          >
            <Rocket size={14} />
            Setup Guide
            {showSetup ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          <ExportButton orders={metrics.filteredOrders} dateRange={dateRange} />
        </div>
      </div>

      {/* Collapsible setup banner */}
      {showSetup && <SetupBanner />}

      {/* Date range filter */}
      <DateRangeFilter dateRange={dateRange} onChange={setDateRange} />

      {/* KPI cards */}
      <KpiCards
        totalRevenueCents={metrics.totalRevenueCents}
        orderCount={metrics.orderCount}
        avgOrderValueCents={metrics.avgOrderValueCents}
        conversionRate={metrics.conversionRate}
        providerRevenueCents={metrics.providerRevenueCents}
        loading={false}
      />

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-bg-tertiary mb-6 pb-1 overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-base font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'text-gold border-b-2 border-gold'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'operations' && (
        <OperationsView
          metrics={metrics}
          orders={metrics.filteredOrders}
        />
      )}

      {activeTab === 'revenue-time' && (
        <RevenueByTimeTab
          byDay={metrics.revenueByDay}
          byWeek={metrics.revenueByWeek}
          byMonth={metrics.revenueByMonth}
        />
      )}

      {activeTab === 'by-bundle' && (
        <RevenueByBundleTab data={metrics.revenueByBundle} />
      )}

      {activeTab === 'pipeline' && (
        <OrderPipelineTab
          pipeline={metrics.pipeline}
          totalOrders={metrics.filteredOrders.length}
        />
      )}

      {activeTab === 'investor' && (
        <InvestorView
          orders={metrics.allOrders}
          totalRevenueCents={metrics.totalRevenueCents}
          orderCount={metrics.orderCount}
        />
      )}

      {activeTab === 'cfo' && (
        <CfoView
          orders={metrics.allOrders}
          totalRevenueCents={metrics.totalRevenueCents}
        />
      )}

      {activeTab === 'config' && <ConfigPanel />}
    </div>
  );
}

// --- Setup Instructions Banner ---

function SetupBanner({ expanded }: { expanded?: boolean }) {
  const steps = [
    {
      num: '1',
      title: 'Set Firebase Secrets',
      status: 'required',
      detail: `Open a terminal and run these 3 commands:`,
      code: [
        'firebase functions:secrets:set VENDOR_API_SECRET',
        'firebase functions:secrets:set STRIPE_SECRET_KEY',
        'firebase functions:secrets:set STRIPE_WEBHOOK_SECRET',
      ],
      note: 'You\'ll be prompted to enter each value. VENDOR_API_SECRET is the vendor/admin password. Stripe keys come from your Stripe Dashboard > Developers > API keys.',
    },
    {
      num: '2',
      title: 'Deploy Cloud Functions',
      status: 'required',
      detail: 'Deploy the seed function (no secrets needed) and all other functions:',
      code: [
        'cd C:\\Users\\sgvan\\hemologix',
        'npx firebase deploy --only functions --force',
      ],
      note: 'This deploys: seedTestOrders, vendorAuth, vendorGetOrders, createCheckoutSession, stripeWebhook, and all financial functions.',
    },
    {
      num: '3',
      title: 'Seed Test Orders',
      status: 'required',
      detail: 'Navigate to the Seed Data page and click the gold button:',
      code: ['/admin/seed  →  "Seed Test Orders (45)"'],
      note: 'This writes 45 simulated orders to Firestore — 15 customers, 10 bundles ($129-$499), spread over 90 days with realistic status progression.',
    },
    {
      num: '4',
      title: 'View Financial Dashboard',
      status: 'required',
      detail: 'Come back here and authenticate with your vendor password:',
      code: ['/admin/financial  →  Enter vendor password  →  Dashboard populates'],
      note: 'All 7 tabs will show data: Operations, Revenue by Time, By Bundle, Pipeline, Investor, CFO, and Config.',
    },
    {
      num: '5',
      title: 'Deploy Hosting (if not already)',
      status: 'optional',
      detail: 'Push the latest frontend build to Firebase Hosting:',
      code: [
        'cd C:\\Users\\sgvan\\hemologix',
        'npx vite build && npx firebase deploy --only hosting --force',
      ],
      note: 'Only needed if you want to test on the live URL instead of localhost.',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="bg-gradient-to-br from-gold/10 via-bg-card to-bg-card border-2 border-gold/30 rounded-2xl overflow-hidden"
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-gold/20 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gold/20 flex items-center justify-center flex-shrink-0">
          <Rocket size={24} className="text-gold" />
        </div>
        <div>
          <h3 className="text-xl font-display text-gold">
            Financial Dashboard Setup
          </h3>
          <p className="text-base text-text-secondary mt-0.5">
            Follow these steps to get the dashboard running with test data
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className={`px-6 py-5 space-y-6 ${expanded ? '' : 'max-h-[600px] overflow-y-auto'}`}>
        {steps.map((step) => (
          <div key={step.num} className="flex gap-4">
            {/* Step number */}
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm font-bold ${
              step.status === 'optional'
                ? 'bg-bg-elevated text-text-muted border border-bg-tertiary'
                : 'bg-gold/20 text-gold'
            }`}>
              {step.num}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-base font-semibold text-text-primary">
                  {step.title}
                </h4>
                {step.status === 'optional' && (
                  <span className="text-xs px-2 py-0.5 rounded bg-bg-elevated text-text-muted border border-bg-tertiary">
                    Optional
                  </span>
                )}
              </div>
              <p className="text-sm text-text-secondary mb-2">{step.detail}</p>

              {/* Code block */}
              <div className="bg-bg-deep rounded-lg border border-bg-tertiary p-3 mb-2 overflow-x-auto">
                {step.code.map((line, i) => (
                  <div key={i} className="font-mono text-sm text-gold whitespace-pre">
                    {line}
                  </div>
                ))}
              </div>

              {/* Note */}
              <p className="text-sm text-text-muted leading-relaxed">{step.note}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gold/20 bg-bg-card/50">
        <p className="text-sm text-text-muted">
          <span className="text-gold font-medium">After setup:</span>{' '}
          Cloud Functions (computeDailySnapshot, resetDailyMetrics) will run on schedule.
          The Stripe webhook will auto-update real-time metrics on live payments.
          QBO integration and pharmacy reconciliation are stubbed — ready to connect when credentials arrive.
        </p>
      </div>
    </motion.div>
  );
}
